import { Activity, OrgActivity } from "../../../../types"
import { MAP_TYPE_TO_STRING } from "../../../constants"
import HighlightedText from "../../highlighted-text"

export const ActivityDescription = ({
  activity,
  hideOwner = false
}: {
  activity: Activity
  hideOwner?: boolean
}) => {
  const {
    type,
    detail,
    category,
    email,
    first_name,
    last_name,
    loan_officer_name
  } = activity

  // Support both new schema (data at root level) and legacy schema (data in detail object)
  const displayEmail =
    email || detail?.subscription?.email || detail?.lead?.email
  const displayFirstName =
    first_name || detail?.subscription?.first_name || detail?.lead?.first_name
  const displayLastName =
    last_name || detail?.subscription?.last_name || detail?.lead?.last_name
  const displayName =
    displayFirstName && displayLastName
      ? `${displayFirstName} ${displayLastName}`
      : displayEmail
  // Use loan_officer_name from root level (new API) or fallback to detail.lo.name (legacy)
  const loName = loan_officer_name || detail?.lo?.name

  const ownerDot = !hideOwner && loName && (
    <>
      <svg
        className="inline-block mx-1"
        width="4"
        height="4"
        viewBox="0 0 4 4"
        fill="none"
      >
        <circle cx="2" cy="2" r="2" fill="#D9D9D9" />
      </svg>
      <span className="font-medium">{loName}</span>
    </>
  )

  switch (type) {
    case "unsubscribed":
      return (
        <>
          <HighlightedText>{displayName}</HighlightedText>
          <span className="text-gray-400 md:text-base-dark">
            {` ${MAP_TYPE_TO_STRING[type]} `}
          </span>
          {!hideOwner && loName && <HighlightedText>{loName}</HighlightedText>}
          <span className="text-gray-400 md:text-base-dark">
            {` from ${category}`}
          </span>
        </>
      )
    case "dashboard_first_visit":
    case "dashboard_initial_visit":
      return (
        <>
          <HighlightedText>{displayName || displayEmail}</HighlightedText>
          <span className="text-gray-400 md:text-base-dark">
            {` ${MAP_TYPE_TO_STRING[type]}`}
          </span>
          {ownerDot}
        </>
      )
    case "intro_email_sent":
    case "sms_followup_sent":
      return (
        <>
          <HighlightedText>{displayName || displayEmail}</HighlightedText>
          <span className="text-gray-400 md:text-base-dark">
            {` ${MAP_TYPE_TO_STRING[type]}`}
          </span>
          {ownerDot}
        </>
      )
    case "loan_summary_jpeg":
      return (
        <>
          {loName && !hideOwner ? (
            <>
              <HighlightedText>{loName}</HighlightedText>
              <span className="text-gray-400 md:text-base-dark">
                {` ${MAP_TYPE_TO_STRING[type]} for `}
              </span>
              <HighlightedText>{displayEmail}</HighlightedText>
            </>
          ) : (
            <>
              <HighlightedText>{displayName || displayEmail}</HighlightedText>
              <span className="text-gray-400 md:text-base-dark">
                {` ${MAP_TYPE_TO_STRING[type]}`}
              </span>
            </>
          )}
        </>
      )
    case "loan_summary_pdf":
    case "intro_email_opened":
    case "intro_email_clicked":
      return (
        <>
          {loName && !hideOwner ? (
            <>
              <HighlightedText>{loName}'s</HighlightedText>
              <span className="text-gray-400 md:text-base-dark">
                {` ${MAP_TYPE_TO_STRING[type]} by `}
              </span>
              <HighlightedText>{displayEmail}</HighlightedText>
            </>
          ) : (
            <>
              <HighlightedText>{displayName || displayEmail}</HighlightedText>
              <span className="text-gray-400 md:text-base-dark">
                {` ${MAP_TYPE_TO_STRING[type]}`}
              </span>
            </>
          )}
        </>
      )
    case "new_lead":
      return (
        <>
          <HighlightedText>{displayName}</HighlightedText>
          <span className="text-gray-400 md:text-base-dark">
            {` ${MAP_TYPE_TO_STRING[type]}`}
          </span>
          {ownerDot}
        </>
      )
    case "new_subscription":
      return (
        <>
          <HighlightedText>{displayName}</HighlightedText>
          <span className="text-gray-400 md:text-base-dark">
            {` ${MAP_TYPE_TO_STRING[type]}`}
          </span>
          {ownerDot}
        </>
      )
    default: {
      const actionText = MAP_TYPE_TO_STRING[type] || type.replace(/_/g, " ")
      return (
        <>
          <HighlightedText>{displayName || displayEmail}</HighlightedText>
          <span className="text-gray-400 md:text-base-dark">
            {` ${actionText}`}
          </span>
          {ownerDot}
        </>
      )
    }
  }
}

export const OrgActivityDescription = ({
  activity
}: {
  activity: OrgActivity
}) => {
  const { type, lo, details } = activity
  switch (type) {
    case "lead":
      return (
        <>
          <HighlightedText>{lo?.name}</HighlightedText>
          <span className="text-gray-400 md:text-base-dark">
            {` ${MAP_TYPE_TO_STRING[type]} `}
          </span>
          <HighlightedText>{`${details.lead?.first_name} ${details.lead?.last_name}`}</HighlightedText>
          <span className="text-gray-400 md:text-base-dark">
            {` from ${details.source_details?.type} ${details.source_details?.title}`}
          </span>
        </>
      )

    default:
      return null
  }
}
